import React, {useState} from 'react'
import PropTypes from 'prop-types'
import {Button, InputNumber} from 'antd'
import {DownOutlined, UpOutlined} from '@ant-design/icons'
import {URGENCY_GROUP_COLORS} from '../../constants'
import {formatInt} from '../../helpers/format'

/**
 * Vùng nhập tỷ lệ hoàn thành theo từng cột mức khẩn — FR-M17-18, chỉ có ở nguồn Sản lượng hiện tại.
 *
 * ⚠️ Dựng ĐỘNG từ `layout` do backend trả về, không hardcode 18 cột (BR-N3-04, E-N3-06). Bộ cột của
 *    màn hình nguồn có thể đổi khi nghiệp vụ đổi; hardcode ở đây nghĩa là lần đó phải sửa cả hai
 *    repo, và nếu quên một bên thì người dùng nhập tỷ lệ cho một cột không còn tồn tại.
 *
 * Thứ tự nhóm và thứ tự cột giữ NGUYÊN như backend gửi xuống — SRS-002 §5 yêu cầu đối chiếu vùng
 * nhập NÀY với MÀN HÌNH NGUỒN "Sản lượng hiện tại" (M17 / FAC-M17-SRS-002) bằng mắt mà không phải
 * dò, nên không được sắp lại theo bất cứ tiêu chí nào ở đây.
 *
 * ⚠️ Ràng buộc này KHÔNG áp cho tab Phân bổ phía dưới. Bảng đó sắp giảm dần theo mức ưu tiên xử lý
 *    (`AllocationTab` — `defaultSortOrder: 'descend'`) nên đọc ngược chiều vùng này; đó là chủ ý đã
 *    được PO chốt, không phải lỗi. Hai bảng trả lời hai câu hỏi khác nhau — vùng này là NHẬP liệu
 *    nên bám thứ tự nguồn, bảng kia là ĐỌC nên bám mức ưu tiên.
 */
const MIN = 0
const MAX = 100

/** Hợp lệ: 0–100, tối đa một chữ số thập phân (BR-N3-05 / §4.3). */
const isValidRatio = (value) =>
    typeof value === 'number' && Number.isFinite(value) && value >= MIN && value <= MAX &&
    Math.abs(value * 10 - Math.round(value * 10)) < 1e-9

const RATIO_ERROR = 'Tỷ lệ hoàn thành trong khoảng 0 đến 100%, tối đa một chữ số thập phân'

/**
 * Dấu phẩy → dấu chấm.
 *
 * Cả màn hình hiển thị số theo vi-VN nên gõ "20,5" là phản xạ tự nhiên. Không đổi thì `InputNumber`
 * đọc ra NaN và ô im lặng không nhận giá trị — người dùng gõ xong thấy số cũ trơ ra, không hiểu vì sao.
 */
const commaToDot = (value) => String(value ?? '').replace(/,/g, '.')

/**
 * Cộng số item của cả một nhóm mức khẩn.
 *
 * Cùng quy ước với từng ô: `qty` là quy mô gốc, `target` là phần sẽ đưa vào tính theo tỷ lệ đang gõ.
 * CỐ Ý không đưa `actual` ra mặt: nguyên tắc lấy trọn đơn kéo thêm item nằm chung đơn, nên đặt con
 * số đó cạnh tổng phần trăm trông như hệ thống cộng sai — nó nằm ở tooltip, giống hệt cách từng ô
 * đang làm.
 */
const sumGroup = (group, counts) =>
    group.columns.reduce(
        (acc, column) => {
            const count = counts[`${group.key}.${column.key}`] || {}

            return {
                qty: acc.qty + (count.qty || 0),
                target: acc.target + (count.target || 0),
                actual: acc.actual + (count.actual || 0),
            }
        },
        {qty: 0, target: 0, actual: 0}
    )

function UrgencyRatioPanel({layout, ratios, counts, onChange, disabled}) {
    const [invalid, setInvalid] = useState({})
    /**
     * Thu gọn được, và MỞ sẵn.
     *
     * Vùng này cao gần một màn hình (3 nhóm, 18 ô), nên khi đã chốt xong tỷ lệ và chỉ muốn đọc bảng
     * kết quả thì nó đẩy bảng ra khỏi tầm mắt. Nhưng mở sẵn vẫn là mặc định đúng: đây là bộ điều
     * khiển chính của nguồn này, thu sẵn thì người mới vào không biết nó tồn tại.
     */
    const [collapsed, setCollapsed] = useState(false)

    const markInvalid = (key, bad) =>
        setInvalid((prev) => {
            if (Boolean(prev[key]) === bad) return prev

            const next = {...prev}

            if (bad) next[key] = true
            else delete next[key]

            return next
        })

    const handleCellChange = (cellKey, value) => {
        if (!isValidRatio(value)) {
            // AC-18.5: báo lỗi và KHÔNG áp dụng giá trị — không tự kẹp về 100 rồi im lặng, vì người
            // dùng sẽ tưởng con số họ gõ đã được nhận.
            markInvalid(cellKey, true)

            return
        }

        markInvalid(cellKey, false)
        onChange(cellKey, value)
    }

    return (
        <div className="VcUrgencyPanel">
            <div className="VcUrgencyPanel__title">
                <span>Tỷ lệ hoàn thành theo từng cột mức khẩn</span>
                <Button size="small" type="link" onClick={() => setCollapsed(!collapsed)}>
                    {collapsed ? 'Mở rộng' : 'Thu gọn'} {collapsed ? <DownOutlined/> : <UpOutlined/>}
                </Button>
            </div>

            {/*
              * Lưới nhóm: 3 cột trên màn rộng, 2 rồi 1 khi hẹp dần.
              *
              * Xếp dọc thì vùng này cao gần một màn hình (3 nhóm) và đẩy bảng kết quả ra khỏi tầm
              * mắt, trong khi mép phải bỏ trống hơn nửa chiều ngang — nhóm rộng nhất cũng chỉ 8 ô.
              *
              * ⚠️ Lưới KHÔNG sắp xếp lại gì: `grid-auto-flow` mặc định theo hàng nên thứ tự đọc
              *    trái→phải vẫn đúng thứ tự backend gửi xuống, giữ nguyên ràng buộc SRS-002 §5
              *    (đối chiếu hai màn hình bằng mắt, không phải dò).
              */}
            {!collapsed && (
            <div className="VcUrgencyPanel__groups">
            {layout.map((group) => {
                const total = sumGroup(group, counts)
                // Cùng luật với từng ô: bằng nhau thì một số là đủ, khác nhau mới hiện `mục tiêu/gốc`.
                const totalShowBoth = total.target !== total.qty

                return (
                <div className="VcUrgencyGroup" key={group.key}>
                    <div
                        className="VcUrgencyGroup__head"
                        style={{background: URGENCY_GROUP_COLORS[group.color] || URGENCY_GROUP_COLORS.green}}
                    >
                        <span className="VcUrgencyGroup__name">{group.label}</span>
                        {/* Tổng cả nhóm — để cân nhắc tỷ lệ theo quy mô nhóm chứ không chỉ từng cột. */}
                        <span
                            className="VcUrgencyGroup__total"
                            title={
                                total.actual !== total.target
                                    ? `Mục tiêu ${formatInt(total.target)} item. Thực tế đưa vào tính ${formatInt(total.actual)} item — chênh lệch do lấy trọn đơn.`
                                    : undefined
                            }
                        >
                            {totalShowBoth
                                ? `${formatInt(total.target)}/${formatInt(total.qty)}`
                                : formatInt(total.qty)} item
                        </span>
                    </div>

                    <div className="VcUrgencyGroup__row">
                        {group.columns.map((column) => {
                            const cellKey = `${group.key}.${column.key}`
                            const value = ratios[cellKey]
                            const count = counts[cellKey] || {}
                            const qty = count.qty || 0
                            const target = count.target || 0
                            const actual = count.actual || 0
            /*
                             * Con số cạnh ô nhập là số lượng MỤC TIÊU — chính tỷ lệ vừa gõ, làm tròn
                             * lên theo từng SKU. Gõ 20% vào cột 457 item thì phải thấy 92/457.
                             *
                             * CỐ Ý không dùng số thực tế ở đây: nguyên tắc lấy trọn đơn có thể kéo
                             * thêm rất nhiều item vào cột (đo được 92 → 453 trên dữ liệu thật), và
                             * đặt con số đó ngay cạnh ô ghi "20%" thì trông như hệ thống tính sai.
                             * Phần chênh được nói ở tooltip, ở dòng tóm tắt và ở tab Phân bổ — những
                             * chỗ có đủ chỗ để giải thích vì sao.
                             *
                             * Đặt 100% thì mục tiêu luôn bằng gốc nên hiện một số là đủ.
                             */
                            const showBoth = target !== qty
                            const hint =
                                actual !== target
                                    ? `Mục tiêu ${formatInt(target)} item. Thực tế đưa vào tính ${formatInt(actual)} item — chênh lệch do lấy trọn đơn: item nằm chung đơn với item đã chọn cũng được lấy.`
                                    : undefined

                            return (
                                <label className="VcUrgencyCell" key={cellKey}>
                                    <span className="VcUrgencyCell__label">{column.label}</span>
                                    {/*
                                      * Đơn vị `%` là ADDON rời, không nhét vào giá trị bằng
                                      * `formatter`. Nhét vào trong thì ký tự `%` nằm lẫn với con số
                                      * đang gõ: bôi đen sửa số là dính cả nó, và mỗi lần gõ lại phải
                                      * bóc ra bằng `parser` — một vòng chuyển đổi chỉ để hiển thị.
                                      * Tách ra thì ô nhập chỉ còn chứa đúng con số.
                                      */}
                                    <span className="VcUrgencyCell__group">
                                        <InputNumber
                                            size="small"
                                            min={MIN}
                                            max={MAX}
                                            step={0.5}
                                            disabled={disabled}
                                            className={invalid[cellKey] ? 'VcUrgencyCell--invalid' : ''}
                                            value={value === undefined ? MAX : value}
                                            parser={commaToDot}
                                            onChange={(next) => handleCellChange(cellKey, next)}
                                        />
                                        <span className="VcUrgencyCell__unit">%</span>
                                    </span>
                                    {/* Quy mô thật của cột, để cân nhắc tỷ lệ dựa trên số item chứ
                                      * không chỉ nhìn con số phần trăm trừu tượng. */}
                                    <span className="VcUrgencyCell__qty" title={hint}>
                                        {showBoth ? `${formatInt(target)}/${formatInt(qty)}` : formatInt(qty)} item
                                    </span>
                                </label>
                            )
                        })}
                    </div>
                </div>
                )
            })}
            </div>
            )}

            {!collapsed && Object.keys(invalid).length > 0 && (
                <div className="VcUrgencyPanel__error">{RATIO_ERROR}</div>
            )}

            {/*
              * Thu gọn rồi vẫn phải thấy mình đang chạy kịch bản nào — ẩn hẳn thì người dùng tưởng
              * mọi cột đang 100% và đọc sai toàn bộ con số bên dưới.
              */}
            {collapsed && (
                <div className="VcUrgencyPanel__summary">
                    {(() => {
                        const changed = layout
                            .flatMap((group) => group.columns.map((column) => ({group, column})))
                            .filter(({group, column}) => {
                                const value = ratios[`${group.key}.${column.key}`]

                                return value !== undefined && value !== MAX
                            })

                        if (!changed.length) return 'Mọi cột đang đặt 100%.'

                        return `${changed.length} cột đang đặt khác 100%: ${changed
                            .map(({group, column}) => `${group.label} · ${column.label} = ${ratios[`${group.key}.${column.key}`]}%`)
                            .join(' · ')}`
                    })()}
                </div>
            )}

            {/*
              * Cột Tổng của mỗi nhóm CỐ Ý không có ở đây (AC-18.3): nó là số tính ra, không phải số
              * nhập vào. Người dùng thấy tổng ở dòng tóm tắt ngay bên dưới.
              */}
        </div>
    )
}

UrgencyRatioPanel.propTypes = {
    layout: PropTypes.arrayOf(
        PropTypes.shape({
            key: PropTypes.string.isRequired,
            label: PropTypes.string.isRequired,
            color: PropTypes.string,
            columns: PropTypes.arrayOf(
                PropTypes.shape({key: PropTypes.string.isRequired, label: PropTypes.string.isRequired})
            ).isRequired,
        })
    ).isRequired,
    ratios: PropTypes.object.isRequired,
    /**
     * Số lượng theo ô, khoá `<nhóm>.<cột>`: `{qty}` là số item gốc, `{actual}` là số thực tế được đưa
     * vào tính sau khi áp tỷ lệ (đã gồm phần làm tròn lên và phần kéo thêm do lấy trọn đơn).
     */
    counts: PropTypes.object,
    onChange: PropTypes.func.isRequired,
    disabled: PropTypes.bool,
}

UrgencyRatioPanel.defaultProps = {disabled: false, counts: {}}

export default UrgencyRatioPanel
